import {
  ConnectionClosedDetail,
  LOGOUT_REASON_RECONNECT_GIVEUP,
  LogoutContext,
  retireSession,
  Session,
  SessionType,
  useAuth,
  ValidatedSessions,
  SessionErrorDetail,
  SessionErrorEventDetail,
} from '@ais-ui/auth';
import { FullScreenLoadingSpinner } from '@ais-ui/ui-core';

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { periodicCheck } from './ol-server/checkSearchAndRescue';
import { closeAllPopups } from '@ais-ui/popups';
import { useGracefulLogout } from './useGracefulLogout';
import * as Sentry from '@sentry/react';
import {
  getMsSinceLastFrame,
  useOlSessionKeepAlive,
} from './useOlSessionKeepAlive';
import { SessionStateRestoreProvider } from './sessionStateRestore/SessionStateRestoreProvider';

const SessionContext = createContext<ValidatedSessions | null>(null);

/**
 * What a `sessionError` signal should trigger:
 *
 * A `sessionError` is only ever dispatched for a backend auth signal — the
 * server has explicitly told us this session is no longer valid
 * (`SessionNotFound` / invalid session, `NewLogonDetected`, `SessionTimeout`).
 * On the load-bearing `olSession` that is terminal: we log the user out and
 * tell them why. There is deliberately **no** reconnect-and-revalidate grace
 * period for these — the grace period exists only for a *dropped transport*
 * (`connectionClosed`), where the session may still be valid on another
 * olserver replica mid-rollout (CLASSIC-10283).
 *
 * - `'immediate-logout'` — any auth signal on `olSession`.
 * - `'ignore'` — anything reported on `dbSession`/`replaySession`: those two
 *   connections are non-critical and must **never** log the user out — we
 *   accept they can be down. Their transport drops still reconnect via the
 *   `connectionClosed` handler, and the load-bearing `olSession` independently
 *   carries (and acts on) any genuine whole-session signal.
 */
export type SessionErrorAction = 'immediate-logout' | 'ignore';

export function resolveSessionErrorAction(
  detail: SessionErrorDetail,
): SessionErrorAction {
  if (detail.sessionType !== 'olSession') {
    return 'ignore';
  }
  return 'immediate-logout';
}

/**
 * True when a `connectionClosed` comes from a socket that no longer owns its
 * slot - one left behind by an earlier login, or a failed reconnect attempt -
 * while the slot holds a different socket that is still usable. Acting on it
 * would wipe the live session (orphaning its socket, which nothing would then
 * retire) and start a spurious reconnect. A slot whose socket is itself dead
 * has nothing live to lose, so a close is acted on as usual.
 */
export function isCloseFromSupersededSocket(
  slot: Session | undefined,
  closedSocket: WebSocket | undefined,
): boolean {
  const current = slot?.webSocket;
  if (!current || !closedSocket || current === closedSocket) {
    return false;
  }
  return (
    current.readyState === WebSocket.OPEN ||
    current.readyState === WebSocket.CONNECTING
  );
}

/**
 * True when a `sessionError` is about a login other than the current one. A
 * close is about a socket, but the backend refuses a sessionId: a reconnect
 * started for an earlier login can still be priming its socket when a new
 * login lands, and logging in again is exactly what makes the backend answer
 * that earlier login with `NewLogonDetected`. Which socket carried the answer
 * says nothing about which login it concerns. A signal whose login is unknown
 * (a socket no session was created for yet) is acted on as usual.
 */
export function isAboutAnotherLogin(
  signalSessionId: string | undefined,
  currentSessionId: string | undefined,
): boolean {
  return signalSessionId !== undefined && signalSessionId !== currentSessionId;
}

export interface SessionProviderProps {
  children: ReactNode;
  logout?: (reason: string, context?: LogoutContext) => void;
  reconnect?: (server: string) => void;
}

export const SessionProvider = ({
  children,
  logout,
  reconnect,
}: SessionProviderProps) => {
  // `username` (not `credentials?.username`, which is only ever populated on
  // localhost — see AuthProvider) is reliably set for both a fresh login and
  // a page-refresh-restored session, which the grace period must handle too.
  const { sessions, setSessions, username } = useAuth();

  // Latest-ref of the committed sessions, so the window-event handlers below
  // can compare against the live slot without re-subscribing on every change.
  const sessionsRef = useRef(sessions);
  useEffect(() => {
    sessionsRef.current = sessions;
  }, [sessions]);

  // Closing popups is part of "what a logout means" for this app, not part of
  // the generic grace-period mechanism, so it's composed in here rather than
  // baked into useGracefulLogout.
  const performLogout = (reason: string, context?: LogoutContext) => {
    if (!logout) return;
    closeAllPopups();
    logout(reason, context);
  };

  const { gracefulLogout, isOlSessionDown } = useGracefulLogout({
    sessions,
    username,
    reconnect: (sessionType) => reconnect?.(sessionType),
    logout: performLogout,
  });

  useEffect(() => {
    const handleSessionError = (event: Event) => {
      const customEvent = event as CustomEvent<
        SessionErrorEventDetail | undefined
      >;
      const detail = customEvent?.detail;
      if (!detail?.logoutReason) {
        return;
      }

      if (
        isAboutAnotherLogin(detail.sessionId, sessionsRef.current?.sessionId)
      ) {
        return;
      }

      const action = resolveSessionErrorAction(detail);

      // dbSession/replaySession auth failures never log the user out — we
      // accept those two connections can be down (they still reconnect via the
      // connectionClosed handler). Bail before any logout or telemetry: a
      // rolling deploy can emit a burst of these on the non-critical sockets.
      if (action === 'ignore') {
        return;
      }

      // `logout()` reports every forced logout to Sentry with the categorized
      // reason (session_invalid / new_logon / session_timeout) and the
      // transport snapshot, so no separate capture is needed here — and one
      // per signal would only flood the project during a session storm.
      gracefulLogout(detail.logoutReason, {
        immediate: action === 'immediate-logout',
        sessionType: detail.sessionType,
      });
    };

    window.addEventListener('sessionError', handleSessionError);
    return () => window.removeEventListener('sessionError', handleSessionError);
  }, [gracefulLogout]);

  // Ensure all popups are closed when the main window/tab is closed or reloaded
  useEffect(() => {
    const handleBeforeUnload = () => {
      try {
        closeAllPopups();
      } catch (e) {
        console.error('Failed to close popups on unload:', e);
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    // pagehide is more reliable on some mobile browsers
    window.addEventListener('pagehide', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handleBeforeUnload);
    };
  }, []);

  // What a lost transport means, whoever noticed it: `onclose`, or the
  // keep-alive giving up on a socket that never closed.
  const handleSessionClosed = useCallback(
    (sessionType: SessionType, webSocket: WebSocket | undefined) => {
      if (
        isCloseFromSupersededSocket(
          sessionsRef.current?.[sessionType],
          webSocket,
        )
      ) {
        return;
      }

      // We set the sessionType to undefined to indicate that the session is closed
      setSessions((prev) =>
        isCloseFromSupersededSocket(prev[sessionType], webSocket)
          ? prev
          : { ...prev, [sessionType]: undefined },
      );

      // olSession is the one session that's load-bearing for the whole app —
      // give it a grace period instead of reconnecting/logging out unconditionally.
      // dbSession/replaySession keep today's behavior: just reconnect.
      if (sessionType === 'olSession') {
        gracefulLogout(LOGOUT_REASON_RECONNECT_GIVEUP, {
          sessionType: 'olSession',
        });
        return;
      }

      reconnect?.(sessionType);
    },
    [gracefulLogout, reconnect, setSessions],
  );

  useEffect(() => {
    const handleConnectionClosed = (event: Event) => {
      const { detail } = event as CustomEvent<
        ConnectionClosedDetail | undefined
      >;
      if (!detail?.sessionType) {
        return;
      }
      handleSessionClosed(detail.sessionType, detail.webSocket);
    };

    window.addEventListener('connectionClosed', handleConnectionClosed);
    return () => {
      window.removeEventListener('connectionClosed', handleConnectionClosed);
    };
  }, [handleSessionClosed]);

  // Keeps the olserver from idling the session out (so the user can walk away
  // from the map), and notices a socket that died without closing. Retiring it
  // first settles its in-flight requests and silences a late `onclose`.
  const handleOlSessionUnresponsive = useCallback(
    (session: Session) => {
      // Retiring is silent to the user, so this is the one record of it. The
      // message stays constant (Sentry throttles by message); details go in extra.
      Sentry.captureMessage('Keep-alive retired the olSession socket', {
        level: 'warning',
        tags: { session_type: 'olSession' },
        extra: {
          msSinceLastFrame: getMsSinceLastFrame(session),
          socketReadyState: session.webSocket?.readyState,
          documentVisibility: document.visibilityState,
        },
      });
      retireSession(session, 'keep-alive went unanswered');
      handleSessionClosed('olSession', session.webSocket);
    },
    [handleSessionClosed],
  );
  useOlSessionKeepAlive(sessions?.olSession, handleOlSessionUnresponsive);

  // Handle tab visibility changes - mobile browsers aggressively suspend/close WebSocket
  // connections when tabs are hidden to save battery. When the tab becomes visible again,
  // check each WebSocket and reconnect if needed.
  useEffect(() => {
    if (!reconnect) return;

    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') {
        return;
      }

      for (const sessionType of ['dbSession', 'replaySession'] as const) {
        const session = sessions?.[sessionType];
        if (session?.webSocket) {
          const wsState = session.webSocket.readyState;
          // WebSocket.CLOSED = 3, WebSocket.CLOSING = 2
          if (wsState === WebSocket.CLOSED || wsState === WebSocket.CLOSING) {
            reconnect(sessionType);
          }
        }
      }

      // olSession is load-bearing: route a stale/closed socket through the
      // same grace-period mechanism as the other two trigger points instead
      // of reconnecting directly — see useGracefulLogout for why there's
      // only ever one path that decides this.
      const olWsState = sessions?.olSession?.webSocket?.readyState;
      if (olWsState === WebSocket.CLOSED || olWsState === WebSocket.CLOSING) {
        gracefulLogout(LOGOUT_REASON_RECONNECT_GIVEUP, {
          sessionType: 'olSession',
        });
        return;
      }

      // Also trigger a keep-alive check to validate session is still valid server-side
      if (olWsState === WebSocket.OPEN) {
        try {
          await periodicCheck(sessions.olSession as Session);
        } catch (error) {
          console.error(
            'Keep-alive check failed after visibility change:',
            error,
          );
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [reconnect, sessions, gracefulLogout]);

  // olSession going momentarily falsy just means "reconnecting" — it does
  // NOT mean the map should unmount. We keep serving the last-known-good
  // session (map/children stay put, useSession() consumers keep a valid,
  // non-null value) until either a fresh session arrives or the grace
  // period in useGracefulLogout gives up and calls logout(), which
  // unmounts this provider entirely. Only a genuine first mount — no
  // session has ever been established yet — falls through to the loading
  // fallback below.
  //
  // This is React's documented "adjust state while rendering" pattern
  // (not an effect): calling setState conditionally during render, guarded
  // so it only fires on an actual change, lets the new value take effect
  // in the same pass instead of flashing the fallback for a frame first.
  //
  // Trade-off worth knowing: the retained session object is a snapshot, not
  // a live one — its `olSession.webSocket` can be CLOSED/CLOSING for the
  // whole grace window (that's exactly what triggered the reconnect in the
  // first place). Any useSession() consumer that eagerly fires RPCs during
  // this window may see transient errors on that socket; useGracefulLogout
  // is what's actually working the reconnect in the background, and once it
  // lands a fresh session here, the socket is live again.
  const [lastKnownSessions, setLastKnownSessions] =
    useState<ValidatedSessions | null>(
      sessions?.olSession ? (sessions as ValidatedSessions) : null,
    );
  if (sessions?.olSession && sessions !== lastKnownSessions) {
    setLastKnownSessions(sessions as ValidatedSessions);
  }

  const content = lastKnownSessions ? children : <FullScreenLoadingSpinner />;

  const olSessionStatusValue = useMemo(
    () => ({ isDown: isOlSessionDown }),
    [isOlSessionDown],
  );

  return (
    <OlSessionStatusContext.Provider value={olSessionStatusValue}>
      <SessionContext.Provider value={lastKnownSessions}>
        <SessionStateRestoreProvider
          socketId={lastKnownSessions?.olSession.socketId}
        >
          {content}
        </SessionStateRestoreProvider>
      </SessionContext.Provider>
    </OlSessionStatusContext.Provider>
  );
};

export const useSession = () => {
  const sessionContext = useContext(SessionContext);

  if (!sessionContext) {
    throw new Error('useSession must be used within a SessionProvider');
  }

  return sessionContext;
};

interface OlSessionStatusContextValue {
  /** True exactly when `olSession` is currently unconfirmed — i.e. a grace
   * period (`useGracefulLogout`) is actively pending. Unlike
   * `useSession().olSession`, which stays truthy for the whole grace period
   * (a deliberately retained last-known-good snapshot, so the app doesn't
   * unmount on every blip), this reflects the real, live connectivity
   * state and is what UI that needs to show "olSession is down right now" —
   * e.g. the "OLServer down" badge in MapPanel — should read instead. */
  isDown: boolean;
}

const OlSessionStatusContext =
  createContext<OlSessionStatusContextValue | null>(null);

/**
 * Reads whether `olSession` is currently down (a grace period is pending).
 * Returns `{ isDown: false }` rather than throwing when used outside
 * `SessionProvider` (e.g. accidentally from popup code, which uses the
 * lightweight `SessionContextProvider` instead and never runs
 * `useGracefulLogout`) — mirroring `useServiceAvailability`'s no-provider
 * fallback convention elsewhere in this codebase.
 */
export const useOlSessionStatus = (): OlSessionStatusContextValue => {
  const context = useContext(OlSessionStatusContext);
  return context ?? { isDown: false };
};

/**
 * Lightweight session context provider for popup windows or other contexts
 * where sessions are already established and don't need the full SessionProvider logic.
 *
 * This allows reusing hooks like useCompleteReportExportFetchingProcess in popups.
 */
export interface SessionContextProviderProps {
  children: ReactNode;
  sessions: ValidatedSessions;
}

export const SessionContextProvider = ({
  children,
  sessions,
}: SessionContextProviderProps) => {
  return (
    <SessionContext.Provider value={sessions}>
      {children}
    </SessionContext.Provider>
  );
};
